/** Карточка одного шага цепочки. */

import type { CSSProperties } from 'react';

import type { PlanStop, StopAlert, TravelMode } from '@cdf/shared';

import { TIER_LABEL, carTier } from '../lib/carTier';
import {
  formatClock,
  formatDistance,
  formatDuration,
  formatLiters,
  formatNumber,
} from '../lib/format';

const MODE_ICON: Record<TravelMode, string> = {
  pedestrian: '🚶',
  driving: '🚗',
  taxi: '🚕',
  service: '🛠',
};

const MODE_VERB: Record<TravelMode, string> = {
  pedestrian: 'пешком',
  driving: 'на машине',
  taxi: 'на такси',
  service: 'сервисом',
};

export type StopCardState = 'current' | 'done' | 'skipped' | 'pending';

interface StopCardProps {
  stop: PlanStop;
  state: StopCardState;
  alert?: StopAlert | undefined;
  /** Свежее событие монитора — карточка подсвечивается. */
  flash: boolean;
  busy: boolean;
  /** Порог литров из настроек — от него считается запас на шаге. */
  minLitersForBonus: number;
  onDone(): void;
  onSkip(): void;
  onShowOnMap(): void;
}

export function StopCard({
  stop,
  state,
  alert,
  flash,
  busy,
  minLitersForBonus,
  onDone,
  onSkip,
  onShowOnMap,
}: StopCardProps): JSX.Element {
  const tier = carTier(stop.car, minLitersForBonus);
  const approach = stop.legs.find((leg) => leg.kind === 'to_car');
  const toStation = stop.legs.find((leg) => leg.kind === 'car_to_station');

  return (
    <li
      className={`stop stop--${state}${flash ? ' is-flash' : ''}${
        alert ? ' has-alert' : ''
      }`}
      aria-current={state === 'current' ? 'step' : undefined}
      data-stop={stop.index}
    >
      <button
        type="button"
        className="stop__badge"
        style={{ '--tier': `var(--c-tier-${tier})` } as CSSProperties}
        onClick={onShowOnMap}
        aria-label={`Показать шаг ${stop.index + 1} на карте`}
      >
        {state === 'done' ? '✓' : state === 'skipped' ? '–' : stop.index + 1}
      </button>

      <div className="stop__main">
        <div className="stop__head">
          <h3 className="stop__title">{stop.car.model}</h3>
          {stop.car.plate ? (
            <span className="plate num">{stop.car.plate}</span>
          ) : null}
        </div>

        <p className="stop__fuel">
          <span className={`chip chip--${tier}`}>
            залить {formatLiters(stop.litersToFill)}
          </span>
          <span className="stop__hint">{TIER_LABEL[tier]}</span>
        </p>

        <dl className="stop__meta">
          {approach ? (
            <div className="stop__metaItem">
              <dt>{MODE_ICON[approach.mode]} до машины</dt>
              <dd className="num">
                {formatDuration(approach.durationS)} · {formatDistance(approach.distanceM)}
                <span className="visually-hidden"> {MODE_VERB[approach.mode]}</span>
              </dd>
            </div>
          ) : null}
          {toStation ? (
            <div className="stop__metaItem">
              <dt>⛽ до АЗС</dt>
              <dd className="num">
                {formatDuration(toStation.durationS)} · {formatDistance(toStation.distanceM)}
              </dd>
            </div>
          ) : null}
          <div className="stop__metaItem">
            <dt>Прибытие</dt>
            <dd className="num">{formatClock(stop.etaArriveCarISO)}</dd>
          </div>
          <div className="stop__metaItem">
            <dt>Готово к</dt>
            <dd className="num">{formatClock(stop.etaDoneISO)}</dd>
          </div>
        </dl>

        <p className="stop__station">{stop.station.name}
          {stop.station.address ? <span> · {stop.station.address}</span> : null}
        </p>

        <p className="stop__bonus num">
          +{formatNumber(stop.bonus)} ₽
          <span className="stop__cumulative">
            всего {formatNumber(stop.cumulativeBonus)} ₽
          </span>
        </p>

        {alert ? (
          <p className="stop__alert" role="status">
            <span aria-hidden="true">⚠️ </span>
            {alert.message}
          </p>
        ) : null}

        {state === 'current' ? (
          <div className="stop__actions">
            <button
              type="button"
              className="btn btn--primary"
              onClick={onDone}
              disabled={busy}
            >
              Готово
            </button>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={onSkip}
              disabled={busy}
            >
              Пропустить
            </button>
          </div>
        ) : null}
      </div>
    </li>
  );
}
